import { Button } from "@material-ui/core";
import { Box, MenuItem, Modal, Select, Typography } from "@mui/material";
import {
  ChartLine,
  Coin,
  Info,
  SignOut,
  Trash,
  UserList,
  Warning,
} from "phosphor-react";
import { useContext, useEffect, useState } from "react";
import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import { fget } from "../../API/callsAPI";
import { MovieColabContext } from "../../Context/MovieColabContext";
import MyTeamMembersPageOverview from "../MyTeamMembersPageComponents/MyTeamMembersPageOverview";
import EnterpriseInfoModal from "../MyTeamsBillingPage/EnterpriseInfoModal";
import EnterpriseThankYouModal from "../MyTeamsBillingPage/EnterpriseThankYouModal";
import ManageDowngradeModal from "../MyTeamsBillingPage/ManageDowngradeModal";
import MyTeamsBillingPage from "../MyTeamsBillingPage/MyTeamsBillingPage";
import PickPlanModal from "../MyTeamsBillingPage/PickPlanModal";
import MyTeamsStorageAndUsagePage from "../MyTeamsStorageAndUsageComponents/MyTeamsStorageAndUsagePage";
import ErrorOrSuccessMessage from "../Utilities/ErrorOrSuccessMessage/ErrorOrSuccessMessage.component";
import DeleteTeamModal from "./DeleteTeamModal";
import ManageTeamHeader from "./ManageTeamHeader";
import myTeamsPageStyles from "./MyTeamsPageStyles";

/* ---------------- HELPERS ---------------- */

const isAccountLocked = (lastPaymentDate) => {
  if (!lastPaymentDate) return false;

  const lastPayment = new Date(lastPaymentDate);
  const now = new Date();

  const diffInDays =
    (now.getTime() - lastPayment.getTime()) / (1000 * 60 * 60 * 24);

  return diffInDays > 50;
};

const getWarningConfig = (currentTierDetails, classes) => {
  if (!currentTierDetails) return null;

  const {
    team_status,
    last_payment_date,
    latest_unpaid_invoice_link,
    next_billing_date = "7 February 2026",
  } = currentTierDetails;

  const dateText = new Date(next_billing_date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const locked = isAccountLocked(last_payment_date);

  if (locked) {
    return {
      boxClass: classes.inactiveWarningBox,
      iconColor: "#EF3A3A",
      text: "Your account has been locked due to overdue payments. Update your billing info to regain access.",
      actionText: "Update Billing",
      actionLink: latest_unpaid_invoice_link,
    };
  }

  if (team_status === "past_due") {
    return {
      boxClass: classes.lockWarningBox,
      iconColor: "#EF763A",
      text: `Payment is still pending. Your projects will be locked on ${dateText} if unpaid.`,
      actionText: "Pay Invoice",
      actionLink: latest_unpaid_invoice_link,
    };
  }

  if (team_status === "pending") {
    return {
      boxClass: classes.warningBox,
      iconColor: "#f39c12",
      text: `Your invoice is pending. Please complete payment by ${dateText} to keep your projects active.`,
      actionText: "Pay Invoice",
      actionLink: latest_unpaid_invoice_link,
    };
  }

  return null;
};

/* ---------------- COMPONENT ---------------- */

export default function MyTeamDetailsPageOverview() {
  const { loggedInUser } = useContext(MovieColabContext);
  const { id } = useParams();
  const classes = myTeamsPageStyles();
  const history = useHistory();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const currentTab = queryParams.get("tab");

  const [isDeleteTeamModalOpen, setIsDeleteTeamModalOpen] = useState(false);
  const [isLeaveTeamModalOpen, setIsLeaveTeamModalOpen] = useState(false);
  const [teamDetails, setTeamDetails] = useState();
  const [isTeamDetailsLoaded, setIsTeamDetailsLoaded] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [myTeams, setMyTeams] = useState([]);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [currentTierDetails, setCurrentTierDetails] = useState();
  const [isChoosePlanModalOpen, setIsChoosePlanModalOpen] = useState(false);
  const [isEnterpriseChooseModalOpen, setIsEnterpriseChooseModalOpen] =
    useState(false);
  const [isEnterpriseThankYouModalOpen, setIsEnterpriseThankYouModalOpen] =
    useState(false);

  const [snackValue, setSnackValue] = useState({
    isOpen: false,
    message: "",
    isError: false,
  });

  /* ---------- DATA FETCH ---------- */

  useEffect(() => {
    const handleGetTeams = async () => {
      try {
        const res = await fget({ url: "project-space/" });
        setMyTeams(res?.data?.results);
      } catch (error) {
        console.log(error);
      }
    };
    handleGetTeams();
  }, []);

  useEffect(() => {
    const handleGetTeamDetails = async () => {
      try {
        setIsTeamDetailsLoaded(false);
        const res = await fget({ url: `project-space/${id}` });
        setTeamDetails(res?.data);
        setIsTeamDetailsLoaded(true);
      } catch (error) {
        console.log(error);
      }
    };
    handleGetTeamDetails();
  }, [refreshCounter, id]);

  useEffect(() => {
    const handleGetTierStatus = async () => {
      try {
        const res = await fget({ url: `project-space/${id}/tier-status/` });
        setCurrentTierDetails(res?.data);
      } catch (error) {
        console.log(error);
      }
    };
    handleGetTierStatus();
  }, [id]);

  useEffect(() => {
    setIsAdminUser(teamDetails?.admin?.id == loggedInUser?.id);
  }, [teamDetails, loggedInUser]);

  const handleTabChange = (tab) => {
    const params = new URLSearchParams(location.search);
    params.set("tab", tab);

    history.push({
      pathname: location.pathname,
      search: params.toString(),
    });
  };

  const warningConfig = getWarningConfig(currentTierDetails, classes);

  return (
    <Box className={classes.root}>
      <ErrorOrSuccessMessage snackValue={snackValue} />
      <Box className={classes.contentContainer}>
        <ManageTeamHeader viewFrom="team-details" />

        <Box style={{ display: "flex", gap: "16px", height: "100%" }}>
          {/* SIDEBAR */}
          <Box sx={{ width: "240px", height: "100%" }}>
            <Select
              value={id}
              onChange={(e) => history.push(`/my-teams/${e.target.value}`)}
              className={classes.dropdownStyle}
              MenuProps={{
                PaperProps: {
                  style: {
                    backgroundColor: "#252A38",
                    color: "#fff !important",
                    fontFamily: "Poppins, sans-serif !important",
                  },
                  "& .MuiMenuItem-root": {
                    color: "#fff !important",
                    backgroundColor: "#252A38",
                    fontFamily: "Poppins, sans-serif !important",
                  },
                },
              }}
              fullWidth
            >
              {myTeams?.map((sin) => (
                <MenuItem
                  value={sin.id}
                  className={classes.singleMenuItem}
                  key={sin.id}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="20" height="20" rx="2" fill="#659BD9" />
                  </svg>
                  {sin?.name}
                </MenuItem>
              ))}
            </Select>
            <Box
              className={classes.teamDetailsSidebar}
              style={{ marginTop: "16px" }}
            >
              <Box
                className={classes.sidebarSingleItem}
                sx={{
                  backgroundColor:
                    currentTab === "members" ? "#1B1D28" : "inherit",
                  cursor: "pointer",
                }}
                onClick={() => handleTabChange("members")}
              >
                <UserList />
                <Typography sx={{ color: "#F9F9F9" }}>Members</Typography>
              </Box>
              {currentTierDetails?.tier !== "free" ? (
                <>
                  <Box
                    className={classes.sidebarSingleItem}
                    sx={{
                      backgroundColor:
                        currentTab === "billing" ? "#1B1D28" : "inherit",
                      cursor: "pointer",
                    }}
                    onClick={() => handleTabChange("billing")}
                  >
                    <Coin />
                    <Typography sx={{ color: "#F9F9F9" }}>Billing</Typography>
                  </Box>
                  {/* <Box
                    className={classes.sidebarSingleItem}
                    sx={{
                      backgroundColor:
                        currentTab === "storage" ? "#1B1D28" : "inherit",
                      cursor: "pointer",
                    }}
                    onClick={() => handleTabChange("storage")}
                  >
                    <ChartLine />
                    <Typography sx={{ color: "#F9F9F9" }}>
                      Storage & Usage
                    </Typography>
                  </Box> */}
                </>
              ) : (
                ""
              )}

              <Typography sx={{ color: "#606479", my: 1 }}>
                Danger Zone
              </Typography>

              {!isAdminUser ? (
                <Button
                  className={classes.dangerButton}
                  onClick={() => setIsLeaveTeamModalOpen(true)}
                >
                  <SignOut size={20} />
                  <Typography sx={{ ml: 1 }}>Leave Team</Typography>
                </Button>
              ) : (
                ""
              )}

              {isAdminUser ? (
                <Button
                  className={classes.dangerButton}
                  onClick={() => setIsDeleteTeamModalOpen(true)}
                >
                  <Trash size={20} />
                  <Typography sx={{ ml: 1 }}>Delete Team</Typography>
                </Button>
              ) : (
                ""
              )}
              {currentTierDetails?.tier === "free" ? (
                <Box className={classes.upgradePlanBox}>
                  {/* Info Icon */}
                  <Info size={20} className={classes.upgradeInfoIcon} />

                  {/* Text */}
                  <Typography className={classes.upgradeText}>
                    Switch to a premium plan for additional team seats,
                    projects, storage, and AI credits!
                  </Typography>

                  {/* CTA */}
                  <Button
                    className={classes.upgradeButton}
                    fullWidth
                    onClick={() => setIsChoosePlanModalOpen(true)}
                  >
                    Upgrade Plan
                  </Button>
                </Box>
              ) : (
                ""
              )}
            </Box>
          </Box>

          {/* MAIN CONTENT */}
          <Box sx={{ flex: 1, height: "100%" }}>
            {/* 🔔 WARNING BANNER */}
            {warningConfig && (
              <Box className={warningConfig.boxClass}>
                <Warning size={16} color={warningConfig.iconColor} />
                <Typography
                  style={{
                    fontSize: "14px",
                  }}
                >
                  {warningConfig.text}
                </Typography>
                <a
                  href={warningConfig.actionLink}
                  target="_blank"
                  rel="noreferrer"
                  className={classes.warningAction}
                >
                  {warningConfig.actionText}
                </a>
              </Box>
            )}

            {currentTab === "members" ? (
              <MyTeamMembersPageOverview setSnackValue={setSnackValue} />
            ) : currentTab === "billing" ? (
              <MyTeamsBillingPage
                teamId={id}
                currentTierDetails={currentTierDetails}
                setIsChoosePlanModalOpen={setIsChoosePlanModalOpen}
                setSnackValue={setSnackValue}
              />
            ) : currentTab === "storage" ? (
              <MyTeamsStorageAndUsagePage />
            ) : (
              <MyTeamMembersPageOverview setSnackValue={setSnackValue} />
            )}
          </Box>
        </Box>
      </Box>

      <Modal open={isDeleteTeamModalOpen} className={classes.flexCenter}>
        <DeleteTeamModal
          handleClose={() => setIsDeleteTeamModalOpen(false)}
          teamDetails={teamDetails}
          handleRefresh={() => {
            setRefreshCounter(refreshCounter + 1);
            history.push("/my-teams");
          }}
          setSnackValue={setSnackValue}
        />
      </Modal>
      <Modal
        open={isChoosePlanModalOpen}
        onClose={() => setIsChoosePlanModalOpen(false)}
        className={classes.flexCenter}
      >
        <PickPlanModal
          handleClose={() => setIsChoosePlanModalOpen(false)}
          currentTierDetails={currentTierDetails}
          teamId={id}
          handleOpenEnterpriseChooseModal={() => {
            setIsEnterpriseChooseModalOpen(true);
            setIsChoosePlanModalOpen(false);
          }}
          setSnackValue={setSnackValue}
          teamDetails={teamDetails}
        />
      </Modal>
      <Modal
        open={isEnterpriseChooseModalOpen}
        onClose={() => setIsEnterpriseChooseModalOpen(false)}
        className={classes.flexCenter}
      >
        <EnterpriseInfoModal
          handleClose={() => setIsEnterpriseChooseModalOpen(false)}
          openThankYouModal={() => setIsEnterpriseThankYouModalOpen(true)}
          setSnackValue={setSnackValue}
        />
      </Modal>
      <Modal
        open={isEnterpriseThankYouModalOpen}
        onClose={() => setIsEnterpriseThankYouModalOpen(false)}
        className={classes.flexCenter}
      >
        <EnterpriseThankYouModal
          handleClose={() => setIsEnterpriseThankYouModalOpen(false)}
        />
      </Modal>
    </Box>
  );
}
